import { useState } from 'react';
import { useAuthProvider, useNotify, useRedirect } from 'react-admin';

// Fases del login:
//   'credentials' → email + contraseña (deja sesión sin boMfa)
//   'enroll'      → primer 2FA: mostramos el QR para escanear en la app
//   'code'        → ingresar el código de 6 dígitos (o uno de recuperación)
//   'recovery'    → mostrar UNA vez los códigos de recuperación recién generados
export type AuthPhase = 'credentials' | 'enroll' | 'code' | 'recovery';

export let useAuth = () => {
  let notify = useNotify();
  let redirect = useRedirect();
  let authProvider = useAuthProvider() as any;

  let [loading, setLoading] = useState(false);
  let [resetPassword, setResetPassword] = useState(false);

  let [phase, setPhase] = useState<AuthPhase>('credentials');
  let [qrDataUrl, setQrDataUrl] = useState<string>('');
  let [alreadyEnrolled, setAlreadyEnrolled] = useState(false);
  let [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);

  let goToConsole = () => redirect(`/transactions?filter={"status":"pending"}`);

  // Fase A. La contraseña deja una sesión sin boMfa; después le preguntamos a la
  // web si el admin ya tiene 2FA. Ese mismo llamado valida que sea EL admin: si
  // no, tira 401/403, cerramos la sesión y volvemos al formulario.
  let handleLogin = async (values: any) => {
    try {
      await authProvider?.login?.(values);
    } catch (err: any) {
      notify('ra.auth.sign_in_error', 'warning');
      setLoading(false);
      return;
    }

    try {
      let info = await authProvider.startTwoFactor();
      setQrDataUrl(info?.qrDataUrl ?? '');
      setAlreadyEnrolled(Boolean(info?.alreadyEnrolled));
      setPhase(info?.alreadyEnrolled ? 'code' : 'enroll');
    } catch (err: any) {
      await authProvider?.logout?.().catch?.(() => undefined);
      notify('auth.two_factor.not_authorized', 'warning');
    } finally {
      setLoading(false);
    }
  };

  // Fase B. Verifica el código en la web y canjea el custom token con boMfa. Si
  // es el primer enrolamiento, primero mostramos los códigos de recuperación.
  let handleCode = async (code: string) => {
    if (!code || loading) return;
    setLoading(true);
    try {
      let res = await authProvider.completeTwoFactor(code);
      if (Array.isArray(res?.recoveryCodes) && res.recoveryCodes.length > 0) {
        setRecoveryCodes(res.recoveryCodes);
        setPhase('recovery');
      } else {
        goToConsole();
      }
    } catch (err: any) {
      notify('auth.two_factor.invalid_code', 'warning');
    } finally {
      setLoading(false);
    }
  };

  let handlePasswordReset = async (values: any) => {
    try {
      await authProvider.sendPasswordResetEmail(values);
      notify('auth.forget_password.success_message', 'info');
      setResetPassword(false);
    } catch (err: any) {
      notify('auth.forget_password.error_message', 'warning');
    } finally {
      setLoading(false);
    }
  };

  let handleSubmit = async (values: any) => {
    setLoading(true);
    if (resetPassword) {
      await handlePasswordReset(values);
    } else {
      await handleLogin(values);
    }
  };

  return {
    loading,
    resetPassword,
    setResetPassword,
    handleSubmit,
    // 2FA
    phase,
    qrDataUrl,
    alreadyEnrolled,
    recoveryCodes,
    handleCode,
    goToCodeStep: () => setPhase('code'),
    finishRecovery: goToConsole,
  };
};
